/**
 * MarketplaceTagBreakdown — "By Tag" breakdown table for the reporting dashboard.
 * Super-Admin only. Rendered under MarketplaceDealBreakdown.
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowUpDown, Tag } from "lucide-react";
import { Card, CardContent } from "../../../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table";
import { tagChipStyle } from "./dealTagsApi";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const MarketplaceTagBreakdown = ({ token, filters, revenueVisible = true }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState("spend");
  const [sortDir, setSortDir] = useState("desc");

  const fetchBreakdown = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.ssp_source) params.ssp_source = filters.ssp_source;
      if (filters.dsp) params.dsp = filters.dsp;
      if (filters.deal_name) params.deal_name = filters.deal_name;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const res = await axios.get(`${API}/reporting/marketplace/tags/breakdown`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setData(res.data || []);
    } catch {
      toast.error("Failed to load tag breakdown");
    } finally {
      setLoading(false);
    }
  }, [token, filters]);

  useEffect(() => { fetchBreakdown(); }, [fetchBreakdown]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("desc"); }
  };

  const sorted = [...data].sort((a, b) => {
    const av = a[sortKey] ?? 0;
    const bv = b[sortKey] ?? 0;
    const cmp = typeof av === "number" ? av - bv : String(av).localeCompare(String(bv));
    return sortDir === "asc" ? cmp : -cmp;
  });

  // Hide tags with zero rows to keep the table useful
  const nonEmpty = sorted.filter((r) => r.row_count > 0);

  const columns = [
    { key: "name", label: "Tag" },
    { key: "deal_count", label: "Deals", num: true, w: "w-16" },
    { key: "impressions", label: "Imps", num: true, w: "w-24" },
    { key: "clicks", label: "Clicks", num: true, w: "w-20" },
    { key: "spend", label: "Spend", num: true, prefix: "$", w: "w-24" },
    ...(revenueVisible ? [
      { key: "revenue", label: "Revenue", num: true, prefix: "$", w: "w-24" },
      { key: "margin_pct", label: "Margin%", num: true, suffix: "%", w: "w-20" },
    ] : []),
    { key: "cpm", label: "CPM", num: true, prefix: "$", w: "w-20" },
    { key: "ctr", label: "CTR%", num: true, suffix: "%", w: "w-18" },
    { key: "cpc", label: "CPC", num: true, prefix: "$", w: "w-18" },
  ];

  const fmtVal = (val, col, row) => {
    if (col.key === "name") {
      return (
        <span
          className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded border text-[11px] font-mono"
          style={tagChipStyle(row.tag_id)}
        >
          {val}
        </span>
      );
    }
    if (val == null) return "-";
    if (col.num) {
      const n = Number(val);
      const formatted = col.key === "impressions" || col.key === "clicks" || col.key === "deal_count"
        ? n.toLocaleString()
        : n.toFixed(2);
      return `${col.prefix || ""}${formatted}${col.suffix || ""}`;
    }
    return String(val);
  };

  return (
    <Card className="bg-[#1e2124] border-[#2a2d31]" data-testid="marketplace-tag-breakdown">
      <CardContent className="p-0">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-[#2a2d31]">
          <div className="w-7 h-7 rounded-lg bg-[#a855f7]/15 flex items-center justify-center">
            <Tag className="w-3.5 h-3.5 text-[#a855f7]" />
          </div>
          <div className="flex-1">
            <p className="text-[#ebebeb]/80 text-xs font-mono font-medium">By Tag</p>
            <p className="text-[#ebebeb]/40 text-[10px] font-mono">
              Aggregated performance per tag across all SSPs
            </p>
          </div>
          <span className="text-[#ebebeb]/40 text-[11px] font-mono">
            {nonEmpty.length} tag{nonEmpty.length === 1 ? "" : "s"} with data
          </span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="w-5 h-5 text-[#ff3737] animate-spin" />
          </div>
        ) : nonEmpty.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-[#ebebeb]/40 font-mono text-xs">
              No tagged deals in the current filter range. Create tags in the Tags tab and apply them to deals to see data here.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-[#2a2d31] hover:bg-transparent">
                  {columns.map((col) => (
                    <TableHead
                      key={col.key}
                      className={`text-[#ebebeb]/50 font-mono text-xs cursor-pointer hover:text-[#ebebeb]/80 ${col.w || ""}`}
                      onClick={() => handleSort(col.key)}
                    >
                      <span className="flex items-center gap-1">
                        {col.label}
                        {sortKey === col.key && <ArrowUpDown className="w-3 h-3 text-[#ff3737]" />}
                      </span>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {nonEmpty.map((row) => (
                  <TableRow key={row.tag_id} className="border-[#2a2d31] hover:bg-white/5" data-testid={`tag-row-${row.tag_id}`}>
                    {columns.map((col) => (
                      <TableCell key={col.key} className={`font-mono text-xs ${col.num ? "text-[#ebebeb]" : "text-[#ebebeb]/80"}`}>
                        {fmtVal(row[col.key], col, row)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MarketplaceTagBreakdown;
